import { apiFront } from './api';
import { CollectMethod } from '../types/CollectMethod';

export interface CollectMethodsParams {
  country?: string;
  currency?: string;
  status?: string;
}

export async function fetchCollectMethods<T extends CollectMethodsParams>(
  params?: T
): Promise<CollectMethod[]> {
  const response = await apiFront.get(`collect_methods`, {
    params,
  });
  // Filter out mercadopago gateway CMs since they are deprecated and can't be configured.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return response.data.filter((c: any) => c.type !== 'mercadopago');
}

// We dont have the metadata of each collect method typed yet.
// So for now let pass down anything and any error would be reported by the api
export async function createCollectMethod(
  params: Metadata
): Promise<CollectMethod> {
  const response = await apiFront.post(`collect_methods`, params);
  return response.data;
}

export async function updateCollectMethod(
  params: Metadata
): Promise<CollectMethod> {
  const response = await apiFront.patch(`collect_methods/${params.id}`, params);
  return response.data;
}

export async function createOrUpdateCollectMethod(
  params: Metadata
): Promise<CollectMethod> {
  if (params.id) {
    return updateCollectMethod(params);
  } else {
    return createCollectMethod(params);
  }
}

export interface DefaultCMParams {
  card: string;
  cbu: string;
  ticket: string;
  debin: string;
  qr: string;
  transfer: string;
}
export async function setDefaultCollectMethods(
  params: DefaultCMParams
): Promise<DefaultCMParams> {
  const response = await apiFront.post(`collect_methods/set_defaults`, params);
  return response.data;
}

export interface AggregatorPaymentMethod {
  gateway_id: number;
  media_payment_name: string;
  enabled: boolean;
  assignation_ids: number[];
  cost: number; // added in query selector
  type: string; // added in query selector
}

export interface AggregatorCostConfiguration {
  payment_refund: number;
  payment_retry: number;
  percentage_translate_credit: number;
  percentage_translate_debit: number;
  percentage_translate_coupon: number;
  percentage_translate_transfer: number;
  percentage_translate_debin: number;
  percentage_translate_qr: number;
}

export interface SystemicInstallments {
  cft: number;
  coefficient: number;
  enabled: boolean;
  installments: number;
  media_payment_name: string;
  promo_id: number | null;
  tea: number;
  tna: number;
}

export interface AggregatorPaymentMethodsResponse {
  payment_methods: AggregatorPaymentMethod[];
  cost_configuration: AggregatorCostConfiguration;
  systemic_installments: SystemicInstallments[];
  commercial_installments: SystemicInstallments[];
}

export async function getAggregatorPaymentMethods(): Promise<AggregatorPaymentMethodsResponse> {
  const response = await apiFront.get(
    `collect_methods/agregator_payment_methods`
  );
  return response.data;
  // const data = {
  //   payment_methods: [
  //     {
  //       gateway_id: 276541059,
  //       media_payment_name: 'visa',
  //       enabled: true,
  //       assignation_ids: [12690945, 12690947, 12690948, 12690949],
  //     },
  //     {
  //       gateway_id: 276541059,
  //       media_payment_name: 'visa_debit',
  //       enabled: true,
  //       assignation_ids: [12690946],
  //     },
  //     {
  //       gateway_id: 276541060,
  //       media_payment_name: 'cabal',
  //       enabled: false,
  //       assignation_ids: [],
  //     },
  //     {
  //       gateway_id: 276541060,
  //       media_payment_name: 'cabal_debit',
  //       enabled: false,
  //       assignation_ids: [],
  //     },
  //     {
  //       gateway_id: 276541061,
  //       media_payment_name: 'discovery',
  //       enabled: false,
  //       assignation_ids: [],
  //     },
  //     {
  //       gateway_id: 276541062,
  //       media_payment_name: 'mastercard',
  //       enabled: false,
  //       assignation_ids: [],
  //     },
  //     {
  //       gateway_id: 276541062,
  //       media_payment_name: 'mastercard_debit',
  //       enabled: false,
  //       assignation_ids: [],
  //     },
  //     {
  //       gateway_id: 4766406,
  //       media_payment_name: 'coupon_pagofacil',
  //       enabled: false,
  //       assignation_ids: [],
  //     },
  //     {
  //       gateway_id: 12824849,
  //       media_payment_name: 'qr',
  //       enabled: false,
  //       assignation_ids: [],
  //     },
  //     {
  //       gateway_id: 276540835,
  //       media_payment_name: 'coupon_rapipago',
  //       enabled: false,
  //       assignation_ids: [],
  //     },
  //     {
  //       gateway_id: 276541289,
  //       media_payment_name: 'debin',
  //       enabled: false,
  //       assignation_ids: [],
  //     },
  //     {
  //       gateway_id: 276541291,
  //       media_payment_name: 'transfer',
  //       enabled: false,
  //       assignation_ids: [],
  //     },
  //   ],
  //   cost_configuration: {
  //     payment_refund: 0.0,
  //     payment_retry: 0.0,
  //     percentage_translate_credit: 3.05,
  //     percentage_translate_debit: 3.0,
  //     percentage_translate_coupon: 0.0,
  //     percentage_translate_transfer: 0.0,
  //     percentage_translate_debin: 3.0,
  //     percentage_translate_qr: 0.8,
  //   },
  // };
}

export interface AggregatorPaymentMethodParams {
  gateway_id: number;
  media_payment_name: string;
}

export async function enableAggregatorPaymentMethod(
  params: AggregatorPaymentMethodParams
): Promise<void> {
  const response = await apiFront.post(
    `collect_methods/agregator_enable_payment_method`,
    params
  );
  return response.data;
}

export async function disableAggregatorPaymentMethod(
  params: AggregatorPaymentMethodParams
): Promise<void> {
  const response = await apiFront.post(
    `collect_methods/agregator_disable_payment_method`,
    params
  );
  return response.data;
}

export interface AggregatorPromoParams {
  installments: number;
}

export async function enableAggregatorPromo(
  params: AggregatorPromoParams
): Promise<void> {
  const response = await apiFront.post(
    `collect_methods/agregator_enable_promo`,
    params
  );
  return response.data;
}

export async function disableAggregatorPromo(
  params: AggregatorPromoParams
): Promise<void> {
  const response = await apiFront.post(
    `collect_methods/agregator_disable_promo`,
    params
  );
  return response.data;
}
